import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, 
    faChevronUp, 
    faChevronDown, 
    faTimes,
    faSave
} from '@fortawesome/free-solid-svg-icons';
import './ExtreGenomeModal.css';

const FLAGS = {
    'English': '',
    'Turkish': '',
    'Korean': '',
    'Japanese': '',
    'Chinese': '',
    'Russian': '',
    'Arabic': '',
    'Spanish': ''
};

const ExtreGenomeModal = ({ isOpen, onClose, type, selectedLanguage }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedItems, setExpandedItems] = useState({});
    const [changes, setChanges] = useState(new Map());
    const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', ''
    const [visibleCount, setVisibleCount] = useState(50);
    const [paginationInfo, setPaginationInfo] = useState(null);
    const [isSearching, setIsSearching] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchData();
            setChanges(new Map());
            setSaveStatus('');
            setSearchTerm('');
        }
    }, [isOpen, type, selectedLanguage]);

    useEffect(() => {
        if (!searchTerm) {
            // If search is cleared, just load normal data
            fetchData();
            return;
        }
        
        const delaySearch = setTimeout(() => {
            performSearch(searchTerm);
        }, 500); // Debounce search to avoid too many requests
        
        return () => clearTimeout(delaySearch);
    }, [searchTerm]);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            setData([]); // Clear existing data to prevent mixing
            setVisibleCount(50); // Reset visible count
            
            const endpoint = type === 'traits' 
                ? `https://service3.szapfs.org/FetchGenomicTraitsTranslations?language=${selectedLanguage}`
                : `https://service3.szapfs.org/FetchGenomicDiseaseTranslations?language=${selectedLanguage}&page=0&pageSize=100`;
            
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error(`Failed to fetch ${type} data`);
            }
            
            const result = await response.json();
            if (result.success && result.data) {
                setData(result.data);
                // If there's pagination info for diseases, store it
                if (type === 'diseases' && result.pagination) {
                    setPaginationInfo(result.pagination);
                }
            } else {
                throw new Error(`No ${type} data found`);
            }
        } catch (err) {
            console.error(`Error fetching ${type} data:`, err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const performSearch = async (term) => {
        if (!term) return;
        
        try {
            setIsSearching(true);
            setLoading(true);
            setError(null);
            
            const endpoint = type === 'traits' 
                ? `https://service3.szapfs.org/FetchGenomicTraitsTranslations?language=${selectedLanguage}&search=${encodeURIComponent(term)}`
                : `https://service3.szapfs.org/FetchGenomicDiseaseTranslations?language=${selectedLanguage}&search=${encodeURIComponent(term)}&page=0&pageSize=100`;
            
            const response = await fetch(endpoint);
            if (!response.ok) {
                throw new Error(`Failed to search ${type} data`);
            }
            
            const result = await response.json();
            if (result.success && result.data) {
                setData(result.data);
                setVisibleCount(50); // Reset visible count
                
                // If there's pagination info for diseases, store it
                if (type === 'diseases' && result.pagination) {
                    setPaginationInfo(result.pagination);
                }
            } else {
                throw new Error(`No ${type} search results found`);
            }
        } catch (err) {
            console.error(`Error searching ${type} data:`, err);
            setError(err.message);
        } finally {
            setLoading(false);
            setIsSearching(false);
        }
    };

    const toggleExpand = (itemId) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    const handleTranslationChange = (id, field, value) => {
        setChanges(prev => {
            const newChanges = new Map(prev);
            
            // If we don't have this item yet, create it
            if (!newChanges.has(id)) {
                newChanges.set(id, {});
            }
            
            // Update the field
            newChanges.get(id)[field] = value;
            
            return newChanges;
        });
        
        setSaveStatus('');
    };

    const handleSaveTranslations = async () => {
        if (changes.size === 0) return;

        try {
            setSaveStatus('saving');
            
            // Convert changes Map to array of objects for API
            const changesArray = [];
            changes.forEach((fields, id) => {
                changesArray.push({
                    id,
                    ...fields
                });
            });
            
            // Endpoint depends on type
            const endpoint = type === 'traits' 
                ? 'https://service3.szapfs.org/SaveGenomicTraitsTranslations'
                : 'https://service3.szapfs.org/SaveGenomicDiseaseTranslations';
            
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    translations: changesArray,
                    targetLanguage: selectedLanguage
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to save ${type} translations`);
            }
            
            // Update data with saved changes
            setData(prevData => {
                return prevData.map(item => {
                    const itemChanges = changes.get(item.id);
                    if (itemChanges) {
                        return { ...item, ...itemChanges };
                    }
                    return item;
                });
            });
            
            setChanges(new Map());
            setSaveStatus('saved');
            
            // Reset save status after 2 seconds
            setTimeout(() => {
                setSaveStatus('');
            }, 2000);
            
        } catch (err) {
            console.error(`Error saving ${type} translations:`, err);
            setError(err.message);
            setSaveStatus('');
        }
    };

    const getSaveButtonClass = () => {
        if (saveStatus === 'saving') return 'extregenome-save-button saving';
        if (saveStatus === 'saved') return 'extregenome-save-button saved';
        return 'extregenome-save-button';
    };

    const getSaveButtonText = () => {
        if (saveStatus === 'saving') return 'Saving...';
        if (saveStatus === 'saved') return 'Saved!';
        return `Save Changes (${changes.size})`;
    };

    // No longer filter data client-side when using server-side search
    const filteredData = data;

    // Get visible data (limited number of items)
    const visibleData = filteredData.slice(0, visibleCount);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const loadMore = async () => {
        if (type === 'traits') {
            // For traits, simply increase the visible count
            setVisibleCount(prev => prev + 50);
        } else {
            // For diseases, check if we need to fetch more data from server
            if (paginationInfo && data.length < paginationInfo.total && filteredData.length >= visibleCount) {
                try {
                    setLoading(true);
                    const currentPage = Math.floor(data.length / paginationInfo.pageSize);
                    const nextPage = currentPage + 1;
                    
                    // Include search term if there is one
                    const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
                    
                    const endpoint = `https://service3.szapfs.org/FetchGenomicDiseaseTranslations?language=${selectedLanguage}${searchParam}&page=${nextPage}&pageSize=100`;
                    
                    const response = await fetch(endpoint);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch more disease data`);
                    }
                    
                    const result = await response.json();
                    if (result.success && result.data && result.data.length > 0) {
                        // Append new data to existing data
                        setData(prevData => [...prevData, ...result.data]);
                        // Update pagination info
                        if (result.pagination) {
                            setPaginationInfo(result.pagination);
                        }
                        // Increase visible count
                        setVisibleCount(prev => prev + result.data.length);
                    } else {
                        console.log('No more disease data available');
                    }
                } catch (err) {
                    console.error(`Error fetching more disease data:`, err);
                    setError(`Error loading more data: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            } else {
                // If we already have all data locally, just show more
                setVisibleCount(prev => prev + 50);
            }
        }
    };

    const renderTraitsContent = () => {
        if (loading) {
            return <div className="extregenome-loading">Loading traits data...</div>;
        }
        
        if (error) {
            return <div className="extregenome-error">Error: {error}</div>;
        }
        
        if (filteredData.length === 0) {
            return <div className="extregenome-empty">No traits data found</div>;
        }
        
        return (
            <>
                <div className="extregenome-list">
                    {visibleData.map((trait, index) => {
                        const itemChanges = changes.get(trait.id) || {};
                        
                        return (
                            <div key={`trait-${trait.id || index}`} className="extregenome-item">
                                <div 
                                    className="extregenome-item-header"
                                    onClick={() => toggleExpand(`trait-${trait.id || index}`)}
                                >
                                    <div className="extregenome-item-name">
                                        {trait.Trait_name_English || `Trait ${index + 1}`}
                                        {trait.variants && <span className="extregenome-item-rsid">{trait.variants}</span>}
                                        {trait.genes && <span className="extregenome-item-gene">{trait.genes}</span>}
                                    </div>
                                    <div className="extregenome-item-icon">
                                        <FontAwesomeIcon 
                                            icon={expandedItems[`trait-${trait.id || index}`] ? faChevronUp : faChevronDown} 
                                        />
                                    </div>
                                </div>
                                <div className={`extregenome-item-content ${expandedItems[`trait-${trait.id || index}`] ? 'open' : ''}`}>
                                    <div className="extregenome-translation-section">
                                        {/* Trait Name */}
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Trait Name</div>
                                                <div className="extregenome-item-value">{trait.Trait_name_English || 'No trait name available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Trait Name ({selectedLanguage})</div>
                                                <textarea
                                                    value={changes.get(trait.id)?.[`Trait_name_${selectedLanguage}`] !== undefined 
                                                        ? changes.get(trait.id)[`Trait_name_${selectedLanguage}`] 
                                                        : trait[`Trait_name_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        trait.id, 
                                                        `Trait_name_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    disabled={selectedLanguage === 'English'}
                                                    placeholder={
                                                        selectedLanguage === 'English' 
                                                            ? 'Trait names in English cannot be edited'
                                                            : `${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`
                                                    }
                                                    className={`extregenome-textarea ${selectedLanguage === 'English' ? 'disabled' : ''}`}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Description */}
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Description</div>
                                                <div className="extregenome-item-value">{trait.Description_English || 'No description available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Description ({selectedLanguage})</div>
                                                <textarea
                                                    value={changes.get(trait.id)?.[`Description_${selectedLanguage}`] !== undefined 
                                                        ? changes.get(trait.id)[`Description_${selectedLanguage}`] 
                                                        : trait[`Description_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        trait.id, 
                                                        `Description_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    placeholder={`${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`}
                                                    className="extregenome-textarea"
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Genotype Description */}
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Genotype Description</div>
                                                <div className="extregenome-item-value">{trait.Genotype_description_English || 'No genotype description available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Genotype Description ({selectedLanguage})</div>
                                                <textarea
                                                    value={changes.get(trait.id)?.[`Genotype_description_${selectedLanguage}`] !== undefined 
                                                        ? changes.get(trait.id)[`Genotype_description_${selectedLanguage}`] 
                                                        : trait[`Genotype_description_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        trait.id, 
                                                        `Genotype_description_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    placeholder={`${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`}
                                                    className="extregenome-textarea"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {filteredData.length > visibleCount && (
                    <div className="extregenome-load-more">
                        <button onClick={loadMore}>Load More</button>
                    </div>
                )}
            </>
        );
    };

    const renderDiseasesContent = () => {
        if (loading) {
            return <div className="extregenome-loading">Loading disease data...</div>;
        }
        
        if (error) {
            return <div className="extregenome-error">Error: {error}</div>;
        }
        
        if (filteredData.length === 0) {
            return <div className="extregenome-empty">No disease data found</div>;
        }
        
        return (
            <>
                <div className="extregenome-list">
                    {visibleData.map((disease, index) => {
                        const itemChanges = changes.get(disease.id) || {};
                        
                        return (
                            <div key={`disease-${disease.id || index}`} className="extregenome-item">
                                <div 
                                    className="extregenome-item-header"
                                    onClick={() => toggleExpand(`disease-${disease.id || index}`)}
                                >
                                    <div className="extregenome-item-name">
                                        {disease.Disease_English || `Disease ${index + 1}`}
                                    </div>
                                    <div className="extregenome-item-icon">
                                        <FontAwesomeIcon 
                                            icon={expandedItems[`disease-${disease.id || index}`] ? faChevronUp : faChevronDown} 
                                        />
                                    </div>
                                </div>
                                <div className={`extregenome-item-content ${expandedItems[`disease-${disease.id || index}`] ? 'open' : ''}`}>
                                    <div className="extregenome-translation-section">
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Disease</div>
                                                <div className="extregenome-item-value">{disease.Disease_English || 'No disease name available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Disease ({selectedLanguage})</div>
                                                <textarea
                                                    value={itemChanges[`Disease_${selectedLanguage}`] !== undefined 
                                                        ? itemChanges[`Disease_${selectedLanguage}`] 
                                                        : disease[`Disease_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        disease.id, 
                                                        `Disease_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    disabled={selectedLanguage === 'English'}
                                                    placeholder={
                                                        selectedLanguage === 'English' 
                                                            ? 'Disease names in English cannot be edited'
                                                            : `${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`
                                                    }
                                                    className={`extregenome-textarea ${selectedLanguage === 'English' ? 'disabled' : ''}`}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Epidemiology</div>
                                                <div className="extregenome-item-value">{disease.Epidemiology_English || 'No epidemiology information available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Epidemiology ({selectedLanguage})</div>
                                                <textarea
                                                    value={itemChanges[`Epidemiology_${selectedLanguage}`] !== undefined 
                                                        ? itemChanges[`Epidemiology_${selectedLanguage}`] 
                                                        : disease[`Epidemiology_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        disease.id, 
                                                        `Epidemiology_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    placeholder={`${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`}
                                                    className="extregenome-textarea"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Risk Factors</div>
                                                <div className="extregenome-item-value">{disease.Risk_Factors_English || 'No risk factors information available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Risk Factors ({selectedLanguage})</div>
                                                <textarea
                                                    value={itemChanges[`Risk_Factors_${selectedLanguage}`] !== undefined 
                                                        ? itemChanges[`Risk_Factors_${selectedLanguage}`] 
                                                        : disease[`Risk_Factors_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        disease.id, 
                                                        `Risk_Factors_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    placeholder={`${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`}
                                                    className="extregenome-textarea"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="extregenome-translation-row">
                                            <div className="extregenome-source-text">
                                                <div className="extregenome-item-title">Lifestyle Action and Prevention</div>
                                                <div className="extregenome-item-value">{disease.Lifestyle_Action_and_Prevention_English || 'No lifestyle information available'}</div>
                                            </div>
                                            <div className="extregenome-target-text">
                                                <div className="extregenome-item-title">Lifestyle Action and Prevention ({selectedLanguage})</div>
                                                <textarea
                                                    value={itemChanges[`Lifestyle_Action_and_Prevention_${selectedLanguage}`] !== undefined 
                                                        ? itemChanges[`Lifestyle_Action_and_Prevention_${selectedLanguage}`] 
                                                        : disease[`Lifestyle_Action_and_Prevention_${selectedLanguage}`] || ''}
                                                    onChange={(e) => handleTranslationChange(
                                                        disease.id, 
                                                        `Lifestyle_Action_and_Prevention_${selectedLanguage}`, 
                                                        e.target.value
                                                    )}
                                                    placeholder={`${FLAGS[selectedLanguage] || ''} Enter ${selectedLanguage} translation`}
                                                    className="extregenome-textarea"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {filteredData.length > visibleCount && (
                    <div className="extregenome-load-more">
                        <button onClick={loadMore}>Load More</button>
                    </div>
                )}
            </>
        );
    };

    const renderSearch = () => {
        return (
            <div className="extregenome-search-container">
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="extregenome-search-input"
                />
 
                {isSearching && <div className="extregenome-searching">Searching...</div>}

            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="extregenome-modal-overlay" onClick={onClose}>
            <div className="extregenome-modal" onClick={e => e.stopPropagation()}>
                <div className="extregenome-modal-header">
                    <h2>{type === 'traits' ? 'Genomic Traits Translation' : 'Genomic Diseases Translation'}</h2>
                    <div className="extregenome-header-actions">
                        <button 
                            onClick={handleSaveTranslations} 
                            className={getSaveButtonClass()}
                            disabled={changes.size === 0 || saveStatus === 'saving'}
                        >
                            <FontAwesomeIcon icon={faSave} />
                            <span>{getSaveButtonText()}</span>
                        </button>
                        <button className="extregenome-close-button" onClick={onClose}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>
                <div className="extregenome-modal-search">
                    {renderSearch()}
                </div>
                <div className="extregenome-modal-content">
                    {type === 'traits' ? renderTraitsContent() : renderDiseasesContent()}
                </div>
            </div>
        </div>
    );
};

export default ExtreGenomeModal;
